// --------------------------------------------------------------
// Created On: 2024-05-17
// Author: Dimitra Weinstein
//
// Last Modified: 2025-11-20
// Modified By: Dimitra Weinstein
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useEffect, useState } from "react";
import useApi from "../../hooks/useApi";
import { API, MAX_IMPELLER_ANGLE, MAX_IMPELLER_TRIM, MAX_ITEM_QUANTITY } from "../../constants/miscellaneous";
import apiRequest from "../../utilities/api/apiRequest";
import getApiError from "../../utilities/api/getApiError";
import Spinner from "../Spinner/Spinner";
import Error from "../Error/Error";
import Modal from "../Modal/Modal";
import ModalHeader from "../ModalHeader/ModalHeader";
import ModalBody from "../ModalBody/ModalBody";
import ModalFooter from "../ModalFooter/ModalFooter";
import PartInfo from "./PartInfo/PartInfo";
import useRefreshCart from "../../hooks/useRefreshCart";
import Toast from "../Toast/Toast";
import useReduxSelector from "src/hooks/useReduxSelector";
import stringIsInteger from "../../utilities/stringIsInteger";
import styles from "./PartModal.module.scss";

// Modal for customizing cart details and adding parts to the shopping cart.
export default function PartModal(props: Props): Component {
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [cartQuantityChangedCounter, setCartQuantityChangedCounter] = useState<number>(0);
  const [paintColors, setPaintColors] = useState<PaintColor[]>([]);
  const [defaultPaintColorId, setDefaultPaintColorId] = useState<number | null>(null);
  const currentUser = useReduxSelector((state) => state.currentUser);

  // Refresh the Redux store's cart quantity whenever an item is added to the cart.
  useRefreshCart(() => {
    return cartQuantityChangedCounter > 0;
  }, [cartQuantityChangedCounter]);

  // Set the incoming prop of default paint color to the state managed by this component to reduce logic needed to manage
  // the default paint color when opening and closing modal.
  useEffect(() => {
    if (props.defaultPaintColorId !== undefined) {
      setDefaultPaintColorId(props.defaultPaintColorId);
    }
  }, [props.defaultPaintColorId]);

  // Get company default paint color.
  useApi(
    () => {
      // The search bar will not pass in the default paint color id so we need to grab it. Parts list will pass it in so
      // this will not need to run.
      return props.defaultPaintColorId === undefined;
    },
    {
      url: `${API}/company/${currentUser.companyId}/preference`,
      method: "GET",
    },
    async (response: Response, responseBody: CompanyPreferenceResponseBody) => {
      if (response.ok && responseBody && responseBody.defaultPaintColor !== null) {
        setErrorMessage("");
        setDefaultPaintColorId(responseBody.defaultPaintColor.paintColorId);
        props.onPaintColorChange(props.partId, responseBody.defaultPaintColor.paintColorId);
      } else if (!response.ok) {
        setErrorMessage(await getApiError(response, "Unable to retrieve company preference setting."));
      }
      setLoading(false);
    },
    [props.defaultPaintColorId, props.partId, props.showModal]
  );

  // Retrieve paint colors from database.
  useApi(
    () => {
      return true;
    },
    {
      url: `${API}/paint-color`,
      method: "GET",
    },
    async (response: Response, responseBody: PaintColorResponseBody) => {
      if (response.ok && responseBody) {
        setPaintColors(responseBody.paintColors);
      } else {
        setErrorMessage(await getApiError(response, "Error loading paint colors."));
      }
    },
    []
  );

  // Adds either a single part or multiple parts to the cart.
  function handleAddToCart(
    partId: number | null,
    parts: Part[],
    quantity: string,
    impellerAngle: string | null,
    impellerTrim: string | null
  ): void {
    if (partId !== null && partId > 0) {
      addSinglePartToCart(quantity, impellerAngle, impellerTrim);
    } else {
      addMultiplePartsToCart(parts);
    }
  }

  // Clear all modifiable states on modal close.
  function handleClose(): void {
    setErrorMessage("");
    props.onClose();
  }

  // Adds multiple parts to the cart.
  async function addMultiplePartsToCart(parts: Part[]): Promise<void> {
    // Filter out parts that aren't sellable before adding the parts to the cart.
    const sellableParts: Part[] = [];
    parts.forEach((part) => {
      if (part.isSellable) {
        sellableParts.push(part);
      }
    });

    // Check if all parts are valid.
    let allPartsAreValid = true;
    for (const part of sellableParts) {
      const [errorMessage, validPart] = partIsValid(part.orderQuantity, part.impellerAngle, part.impellerTrim);
      if (!validPart) {
        allPartsAreValid = false;
        setErrorMessage(errorMessage);
        break;
      }
    }

    if (allPartsAreValid) {
      // Construct the request body for the adding the items to the cart.
      const cartItems: CartItem[] = [];
      sellableParts.forEach((part) => {
        cartItems.push({
          partId: part.partId,
          paintColorId: part.isPaintable ? part.paintColorId : null,
          quantity: parseInt(part.orderQuantity, 10),
          impellerTrim: props.isImpeller ? part.impellerTrim : null,
          impellerAngle: props.isImpeller ? part.impellerAngle : null,
          units: part.units,
        });
      });

      const requestBody: RequestBody = {
        replaceAll: false,
        items: cartItems,
      };

      // Add the items to the cart.
      setLoading(true);
      const [response] = await apiRequest(`${API}/cart`, "PUT", requestBody);
      if (response.ok) {
        setErrorMessage("");
        setCartQuantityChangedCounter((previous) => previous + 1);
        handleClose();
      } else {
        setErrorMessage(await getApiError(response, "Unable to add item to cart."));
      }
      setLoading(false);
    }
  }

  // Validate that the impeller angle is within the specified bounds.
  function validateImpellerAngle(impellerAngle: string | null): boolean {
    if (
      (impellerAngle !== null && isNaN(parseFloat(impellerAngle))) ||
      Number(impellerAngle) < 0 ||
      Number(impellerAngle) > MAX_IMPELLER_ANGLE ||
      (impellerAngle !== null && impellerAngle === "") ||
      impellerAngle === "."
    ) {
      return false;
    } else {
      return true;
    }
  }

  // Validate that the impeller trim is within the specified bounds.
  function validateImpellerTrim(impellerTrim: string | null): boolean {
    if (
      (impellerTrim !== null && isNaN(parseFloat(impellerTrim))) ||
      Number(impellerTrim) < 0 ||
      Number(impellerTrim) > MAX_IMPELLER_TRIM ||
      (impellerTrim !== null && impellerTrim === "") ||
      impellerTrim === "."
    ) {
      return false;
    } else {
      return true;
    }
  }

  // Validate that the quantity is within the specified bounds.
  function validateQuantity(quantity: string): boolean {
    if (
      isNaN(parseInt(quantity, 10)) ||
      parseInt(quantity, 10) > MAX_ITEM_QUANTITY ||
      parseInt(quantity, 10) <= 0 ||
      !stringIsInteger(quantity)
    ) {
      return false;
    } else {
      return true;
    }
  }

  // Returns a tuple that includes a possible error message and a boolean that is true if the part is valid.
  function partIsValid(
    quantity: string,
    impellerAngle: string | null,
    impellerTrim: string | null
  ): [errorMessage: string, validPart: boolean] {
    if (!validateQuantity(quantity)) {
      return [`Quantity must be a number between 1 and ${MAX_ITEM_QUANTITY}.`, false];
    } else if (!validateImpellerAngle(impellerAngle)) {
      return [`Impeller angle must be between 0 and ${MAX_IMPELLER_ANGLE} degrees.`, false];
    } else if (!validateImpellerTrim(impellerTrim)) {
      return [`Impeller trim must be between 0 and ${MAX_IMPELLER_TRIM} inches.`, false];
    } else {
      return ["", true];
    }
  }

  // Adds a single item to the cart.
  async function addSinglePartToCart(
    quantity: string,
    impellerAngle: string | null,
    impellerTrim: string | null
  ): Promise<void> {
    // Validate part.
    const [errorMessage, validPart] = partIsValid(quantity, impellerAngle, impellerTrim);
    if (validPart) {
      setLoading(true);
      const requestBody = {
        replaceAll: false,
        items: [
          {
            partId: props.partId,
            paintColorId: props.isPaintable ? props.paintColorId : null,
            quantity: parseInt(props.orderQuantity, 10),
            impellerTrim: props.isImpeller ? impellerTrim : null,
            impellerAngle: props.isImpeller ? impellerAngle : null,
            units: props.unitName,
          },
        ],
      };
      const [response, responseBody] = await apiRequest(`${API}/cart`, "PUT", requestBody);
      if (response.ok && responseBody) {
        setErrorMessage("");
        handleClose();
      } else {
        setErrorMessage(await getApiError(response, "Unable to add item to cart."));
      }
      setCartQuantityChangedCounter((previous) => previous + 1);
      setLoading(false);
    } else {
      setErrorMessage(errorMessage);
    }
  }

  return (
    <Fragment>
      <Spinner loading={loading} />
      <Modal
        show={props.showModal}
        onHide={() => handleClose()}
        style={{ zIndex: "var(--standard-modal-z-index)" }}
        backdropClassName={`${styles.modal} ${styles.backdrop}`}
        size="xl"
        centered
        animation
      >
        <ModalHeader className="bg-primary text-center">
          <h6 className="lead text-white text-center">Order Parts</h6>
        </ModalHeader>
        <ModalBody className={`${styles.modalBody}`}>
          {!props.isAddAllOrderableParts ? (
            <PartInfo
              partId={props.partId}
              partNumber={props.partNumber}
              description={props.description}
              price={props.price}
              companyDiscount={props.companyDiscount}
              orderQuantity={props.orderQuantity}
              unitName={props.unitName}
              selectedPaintColorId={props.paintColorId}
              defaultPaintColorId={defaultPaintColorId}
              isPaintable={props.isPaintable}
              paintColors={paintColors}
              isImpeller={props.isImpeller}
              isSellable={props.isSellable}
              hasOtherMaterial={true}
              impellerAngle={props.impellerAngle}
              impellerTrim={props.impellerTrim}
              weight={props.weight}
              onQuantityChange={(partId, orderQuantity) => props.onQuantityChange(props.partId, orderQuantity)}
              onPaintColorChange={(selectedPaintColorId) =>
                props.onPaintColorChange(props.partId, selectedPaintColorId)
              }
              onImpellerAngleChange={(impellerAngle) => props.onImpellerAngleChange(props.partId, impellerAngle)}
              onImpellerTrimChange={(impellerTrim) => props.onImpellerTrimChange(props.partId, impellerTrim)}
            />
          ) : (
            // Renders all sellable parts under "Add All Items To Cart" option.
            props.parts.map((part, i) => {
              return part.isSellable ? (
                <PartInfo
                  key={i}
                  partId={part.partId}
                  partNumber={part.partNumber}
                  description={part.description}
                  price={part.price}
                  companyDiscount={part.companyDiscount}
                  orderQuantity={part.orderQuantity}
                  unitName={part.units}
                  selectedPaintColorId={part.paintColorId}
                  defaultPaintColorId={defaultPaintColorId}
                  isPaintable={part.isPaintable}
                  paintColors={paintColors}
                  isImpeller={part.isImpeller}
                  isSellable={part.isSellable}
                  hasOtherMaterial={part.hasOtherMaterial}
                  impellerAngle={part.impellerAngle}
                  impellerTrim={part.impellerTrim}
                  weight={part.weight}
                  onQuantityChange={(partId, orderQuantity) => props.onQuantityChange(part.partId, orderQuantity)}
                  onPaintColorChange={(selectedPaintColorId) =>
                    props.onPaintColorChange(part.partId, selectedPaintColorId)
                  }
                  onImpellerAngleChange={(impellerAngle) => props.onImpellerAngleChange(part.partId, impellerAngle)}
                  onImpellerTrimChange={(impellerTrim) => props.onImpellerTrimChange(part.partId, impellerTrim)}
                />
              ) : (
                <div></div>
              );
            })
          )}
          {/* Show error message in modal as a banner if show a single part,
              Show error message in toast otherwise.
           */}
          {errorMessage && props.partId !== 0 ? (
            <div className="my-2">
              <Error message={errorMessage} />
            </div>
          ) : (
            <Toast
              title="Error"
              message={errorMessage}
              show={errorMessage.length > 0}
              onClose={() => setErrorMessage("")}
              type="error"
              data-test="part-modal-error-toast"
            />
          )}
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() =>
              handleAddToCart(props.partId, props.parts, props.orderQuantity, props.impellerAngle, props.impellerTrim)
            }
          >
            Add To Cart
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => handleClose()}>
            Close
          </button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
}

interface Props {
  showModal: boolean;
  partId: number | null;
  partNumber: string;
  description: string;
  buildQuantity: string;
  orderQuantity: string;
  impellerAngle: string | null;
  impellerTrim: string | null;
  unitName: string;
  price: number | null;
  companyDiscount: number | null;
  weight: number | null;
  isPaintable: boolean;
  isSellable: boolean;
  isImpeller: boolean;
  isAddAllOrderableParts: boolean;
  paintColorId: number | null;
  defaultPaintColorId?: number | null;
  parts: Part[];
  onQuantityChange: (partId: number | null, orderQuantity: string) => void;
  onPaintColorChange: (partId: number | null, paintColorId: number | null) => void;
  onImpellerTrimChange: (partId: number | null, impellerTrim: string | null) => void;
  onImpellerAngleChange: (partId: number | null, impellerAngle: string | null) => void;
  onClose: () => void;
}

interface CartItem {
  impellerAngle: string | null;
  impellerTrim: string | null;
  paintColorId: number | null;
  partId: number;
  quantity: number;
  units: string;
}

interface RequestBody {
  replaceAll: boolean;
  items: CartItem[];
}

interface PartFromApi {
  partId: number;
  partNumber: string;
  description: string;
  unitId: number;
  units: string;
  price: number | null;
  companyDiscount?: number | null;
  buildQuantity: string;
  weight: number;
  isPaintable: boolean;
  isSellable: boolean;
  isImpeller: boolean;
  hasOtherMaterial: boolean;
  parts: PartFromApi[];
}

interface Part {
  partId: number;
  partNumber: string;
  description: string;
  unitId: number;
  units: string;
  price: number;
  companyDiscount?: number | null;
  buildQuantity: string;
  paintColorId: number | null;
  orderQuantity: string;
  weight: number;
  isPaintable: boolean;
  isSellable: boolean;
  isImpeller: boolean;
  impellerTrim: string | null;
  impellerAngle: string | null;
  hasOtherMaterial: boolean;
  parts: PartFromApi[];
}

interface PaintColorResponseBody {
  paintColors: PaintColor[];
}

interface PaintColor {
  paintColorId: number;
  hexCode: string;
  name: string;
}

interface CompanyPreferenceResponseBody {
  defaultPaintColor: PaintColor | null;
}
